// Based off: https://codepen.io/mkasumi/pen/JJKvjq
// See it on: https://codesandbox.io/s/pure-css-scroll-hinting-m066x
import React, { ReactNode, forwardRef } from "react";
import styled, { css } from "styled-components";
import { colors } from "@sonder/ui/source/styled/theme";

interface ScrollerProps {
  backgroundColor?: string;
  blendColor?: string;
  blendSize?: string;
  className?: string;
  children: ReactNode;
  direction?: "horizontal" | "vertical" | "both";
  fadeSize?: string;
  shadowColor?: string;
  shadowSize?: string;
}

const HorizontalScroll = css<ScrollerProps>`
  background: linear-gradient(
        90deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      100% / ${props => props.fadeSize} 100%,
    linear-gradient(
        270deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      0 0 / ${props => props.fadeSize} 100%;
  background-size: ${props => props.shadowSize} 100%;
`;

const VerticalScroll = css<ScrollerProps>`
  background: linear-gradient(
        0deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      100% / ${props => props.fadeSize} 100%,
    linear-gradient(
        180deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      0 0 / ${props => props.fadeSize} 100%;
  background-position: top, bottom;
  background-size: 100% ${props => props.shadowSize};
`;

const BothScroll = css<ScrollerProps>`
  background: linear-gradient(
        0deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      100% / ${props => props.fadeSize} 100%,
    linear-gradient(
        180deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      0 0 / ${props => props.fadeSize} 100%,
    linear-gradient(
        90deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      100% / ${props => props.fadeSize} 100%,
    linear-gradient(
        270deg,
        ${props => props.blendColor},
        ${props => props.shadowColor}
      )
      0 0 / ${props => props.fadeSize} 100%;
  background-position: top, bottom, right, left;
  background-size: 100% ${props => props.shadowSize},
    100% ${props => props.shadowSize}, ${props => props.shadowSize} 100%,
    ${props => props.shadowSize} 100%;
`;

const HorizontalContents = css<ScrollerProps>`
  background: linear-gradient(
      90deg,
      ${props => props.backgroundColor} 30%,
      ${props => props.blendColor}
    ),
    linear-gradient(
        270deg,
        ${props => props.backgroundColor} 30%,
        ${props => props.blendColor}
      )
      100% 0;
  background-size: ${props => props.blendSize} 100%;
`;

const VerticalContents = css<ScrollerProps>`
  background: linear-gradient(
      0deg,
      ${props => props.backgroundColor} 30%,
      ${props => props.blendColor}
    ),
    linear-gradient(
        180deg,
        ${props => props.backgroundColor} 30%,
        ${props => props.blendColor}
      )
      100% 0;
  background-position: bottom, top;
  background-size: 100% ${props => props.blendSize};
`;

const BothContents = css<ScrollerProps>`
  background: linear-gradient(
      0deg,
      ${props => props.backgroundColor} 30%,
      ${props => props.blendColor}
    ),
    linear-gradient(
        180deg,
        ${props => props.backgroundColor} 30%,
        ${props => props.blendColor}
      )
      100% 0,
    linear-gradient(
      90deg,
      ${props => props.backgroundColor} 30%,
      ${props => props.blendColor}
    ),
    linear-gradient(
        270deg,
        ${props => props.backgroundColor} 30%,
        ${props => props.blendColor}
      )
      100% 0;
  background-position: bottom, top, left, right;
  background-size: 100% ${props => props.blendSize},
    100% ${props => props.blendSize}, ${props => props.blendSize} 100%,
    ${props => props.blendSize} 100%;
`;

export const ScrollerContent = styled.div`
  background-attachment: local;
  display: flex;
  flex: 1 1 auto;
`;

const StyledScroller = styled.div<ScrollerProps>`
  background-attachment: scroll;
  ${props => props.direction === "horizontal" && HorizontalScroll}
  ${props => props.direction === "vertical" && VerticalScroll}
  ${props => props.direction === "both" && BothScroll}
  background-color: ${props => props.backgroundColor};
  background-repeat: no-repeat;
  display: flex;
  overflow: auto;

  ${ScrollerContent} {
    ${props => props.direction === "horizontal" && HorizontalContents}
    ${props => props.direction === "vertical" && VerticalContents}
    ${props => props.direction === "both" && BothContents}
    background-repeat: no-repeat;
  }

  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Scroller = forwardRef(
  (
    {
      backgroundColor = colors.white,
      blendColor = `rgba(255, 255, 255, 0)`,
      blendSize = `3rem`,
      className,
      children,
      direction = `horizontal`,
      fadeSize = `2rem`,
      shadowColor = `rgba(0, 0, 0, 0.15)`,
      shadowSize = `1rem`,
    }: ScrollerProps,
    ref: React.Ref<HTMLDivElement>,
  ) => (
    <StyledScroller
      backgroundColor={backgroundColor}
      blendColor={blendColor}
      blendSize={blendSize}
      className={className}
      direction={direction}
      fadeSize={fadeSize}
      ref={ref}
      shadowColor={shadowColor}
      shadowSize={shadowSize}
    >
      {children}
    </StyledScroller>
  ),
);
